import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { reaction, IReactionDisposer } from 'mobx';
import { withTranslation, WithTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { RotateLoader } from '@lib/components';
import { MobxComponent } from '../../../../mobx/components/index';

interface Props extends WithTranslation {}

interface State {
  loading: boolean;
  purchase: T.API.NativeLoyaltyGiftCardPurchase | null;
  error: string | null;
}

// ─── Design tokens (match loyalty shell) ────────────────────────────────────
const C = {
  border: '#e9eaeb',
  label: '#181d27',
  muted: '#717680',
  subtle: '#f7f8f9',
  rowBg: '#ffffff',
  red: '#E03B2F',
  navy: '#1a1a54',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '8px 0',
  borderBottom: `1px solid ${C.border}`,
  gap: 12,
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: C.muted,
  fontWeight: 400,
  flexShrink: 0,
};

const valueStyle: React.CSSProperties = {
  fontSize: 12,
  color: C.label,
  fontWeight: 500,
  textAlign: 'right',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: '20px 0 8px',
};

function minorToMajor(amount: number, precision: number): number {
  if (!precision) return amount;
  return amount / Math.pow(10, precision);
}

function fmtMinor(
  amount?: number | null,
  currency?: string,
  precision = 2
): string {
  if (amount === undefined || amount === null) return '—';
  const major = minorToMajor(amount, precision);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(major);
  } catch {
    return major.toFixed(precision);
  }
}

function recipientDisplay(purchase: T.API.NativeLoyaltyGiftCardPurchase): {
  name?: string;
  email?: string;
  phone?: string;
  notes?: string;
} {
  const { recipient, recipientCustomer, customerName, customerEmail } = purchase;
  if (recipientCustomer?.name || recipientCustomer?.email) {
    return {
      name: recipientCustomer.name,
      email: recipientCustomer.email,
      phone: recipientCustomer.phone,
    };
  }
  if (recipient.type === 'self') {
    return { name: customerName, email: customerEmail };
  }
  return {
    name: recipient.name,
    email: recipient.email,
    phone: recipient.phone,
    notes: recipient.message,
  };
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

@inject('store')
@observer
class GiftCardPurchaseDetailDrawerClass extends MobxComponent<Props, State> {
  state: State = { loading: false, purchase: null, error: null };
  private disposeReaction?: IReactionDisposer;

  componentDidMount() {
    this.disposeReaction = reaction(
      () => this.injected.store.notifications.s.modal_item,
      (item) => {
        const isGiftCard =
          item?.verb === 'gift_card_purchase_pending_confirmation' ||
          item?.verb === 'gift_card_purchase_confirmed';
        if (item && isGiftCard) {
          this.fetchPurchase(item);
        } else if (!item) {
          this.setState({ purchase: null, error: null, loading: false });
        }
      },
      { fireImmediately: true }
    );
  }

  componentWillUnmount() {
    this.disposeReaction?.();
  }

  fetchPurchase = async (item: T.Lib.GetStream.NotificationItem) => {
    const { store } = this.injected;
    const restaurant = store.restaurant;
    if (!restaurant) return;

    const purchaseId = (item as any).purchase_id as string | undefined;
    if (!purchaseId) {
      this.setState({ loading: false, purchase: null, error: null });
      return;
    }

    this.setState({ loading: true, purchase: null, error: null });
    try {
      const res = await store.api.native_loyalty_gift_card_purchase_get({
        restaurant_id: restaurant._id,
        purchase_id: purchaseId,
      });
      if (res.outcome === 0 && res.data?.purchase) {
        this.setState({ loading: false, purchase: res.data.purchase, error: null });
      } else {
        // API unavailable or error — fall back to notification-only view
        this.setState({ loading: false, purchase: null, error: null });
      }
    } catch (e) {
      // Native-loyalty endpoint may not exist yet — degrade gracefully
      this.setState({ loading: false, purchase: null, error: null });
    }
  };

  handleClose = () => {
    this.injected.store.notifications.close_modal();
    this.setState({ purchase: null, error: null, loading: false });
  };

  renderRow(label: string, value?: string | number | null) {
    if (value === undefined || value === null || value === '') return null;
    return (
      <div style={rowStyle}>
        <span style={labelStyle}>{label}</span>
        <span style={valueStyle}>{value}</span>
      </div>
    );
  }

  renderBody() {
    const { loading, purchase } = this.state;
    const { modal_item } = this.injected.store.notifications.s;

    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <RotateLoader />
        </div>
      );
    }

    // Fallback: no detailed data yet — show what's in the notification item
    if (!purchase) {
      const purchaseId = (modal_item as any)?.purchase_id as string | undefined;
      return (
        <div style={{ paddingTop: 16 }}>
          <p style={sectionTitleStyle}>Purchase Info</p>
          {modal_item?.body && (
            <p style={{ fontSize: 13, color: C.label, lineHeight: '1.5', margin: '0 0 12px' }}>
              {modal_item.body}
            </p>
          )}
          {purchaseId && this.renderRow('Purchase ID', purchaseId)}
        </div>
      );
    }

    const { currency, currencyPrecision: prec } = purchase;
    const recipient = recipientDisplay(purchase);
    const discountMinor =
      purchase.discountPercent > 0 ? purchase.amount - purchase.amountPaid : 0;

    return (
      <>
        {/* Order Details */}
        <p style={sectionTitleStyle}>Order Details</p>

        {this.renderRow('Placed', fmtDate(purchase.createdAt))}
        {this.renderRow('Total', fmtMinor(purchase.amount, currency, prec))}
        {recipient.name && this.renderRow('Recipients Name', recipient.name)}
        {recipient.email && this.renderRow('Recipients Email', recipient.email)}
        {this.renderRow('Sender', purchase.customerName)}
        {this.renderRow("Sender's Email", purchase.customerEmail)}
        {recipient.phone && this.renderRow('Phone', recipient.phone)}
        {this.renderRow('Payment', purchase.paymentMethod)}

        {/* Order Summary */}
        <p style={sectionTitleStyle}>Order Summary</p>

        {purchase.items.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            {purchase.items.map((item, i) => {
              const lineTotal = item.faceValue * item.quantity;
              const label = `${item.quantity}x ${fmtMinor(item.faceValue, currency, prec)} Gift Card`;
              return (
                <div key={i} style={rowStyle}>
                  <span style={{ ...labelStyle, color: C.label, fontWeight: 600 }}>{label}</span>
                  <span style={{ ...valueStyle, fontWeight: 600 }}>{fmtMinor(lineTotal, currency, prec)}</span>
                </div>
              );
            })}
          </div>
        )}

        {recipient.notes && (
          <div style={{ margin: '8px 0', padding: '8px 10px', background: C.subtle, borderRadius: 6 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: C.muted, margin: '0 0 2px' }}>Notes</p>
            <p style={{ fontSize: 12, color: C.label, margin: 0 }}>{recipient.notes}</p>
          </div>
        )}

        <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8 }}>
          {this.renderRow('Cart', fmtMinor(purchase.amount, currency, prec))}
          {discountMinor > 0 &&
            this.renderRow(
              `Discount${purchase.discountPercent ? ` (${purchase.discountPercent}%)` : ''}`,
              `-${fmtMinor(discountMinor, currency, prec)}`
            )}
          <div style={{ ...rowStyle, borderBottom: 'none', paddingTop: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.label }}>Total</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.label }}>
              {fmtMinor(purchase.amountPaid, currency, prec)}
            </span>
          </div>
        </div>
      </>
    );
  }

  render() {
    const { modal_item } = this.injected.store.notifications.s;
    const isOpen =
      !!modal_item &&
      (modal_item.verb === 'gift_card_purchase_pending_confirmation' ||
        modal_item.verb === 'gift_card_purchase_confirmed');

    if (!isOpen) return null;

    const purchaseId = (modal_item as any)?.purchase_id as string | undefined;
    const { purchase } = this.state;
    const headerName = purchase?.customerName ?? modal_item?.title ?? 'Gift Card Purchase';
    const headerEmail = purchase?.customerEmail;

    return (
      <>
        {/* Backdrop */}
        <div
          onClick={this.handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 999,
          }}
        />

        {/* Side panel */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            width: 420,
            maxWidth: '95vw',
            background: '#fff',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              padding: '16px 20px 12px',
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
            }}
          >
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.label }}>
                {headerName}
              </p>
              {headerEmail && (
                <p style={{ margin: '2px 0 0', fontSize: 12, color: C.muted }}>{headerEmail}</p>
              )}
              {!headerEmail && modal_item?.body && (
                <p style={{ margin: '2px 0 0', fontSize: 12, color: C.muted }}>{modal_item.body}</p>
              )}
              {(purchase?.id || purchaseId) && (
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: 6,
                    padding: '2px 8px',
                    background: C.subtle,
                    border: `1px solid ${C.border}`,
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.navy,
                  }}
                >
                  #{(purchase?.id ?? purchaseId!).slice(-6).toUpperCase()}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={this.handleClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                border: 'none',
                borderRadius: 6,
                background: 'transparent',
                cursor: 'pointer',
                color: C.muted,
                flexShrink: 0,
              }}
            >
              <FaTimes size={14} />
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0 20px 24px',
            }}
          >
            {this.renderBody()}
          </div>
        </div>
      </>
    );
  }
}

export const GiftCardPurchaseDetailDrawer = withTranslation()(GiftCardPurchaseDetailDrawerClass);
